import type { FC, ReactNode } from 'react'
import type { BandMember } from '@/types'
import type { PlayableSource } from '@/lib/audio/engine'

import { useMemo } from 'react'
import { cn } from '@/lib/utils'
import { formatTime } from '@/lib/format'
import { audioEngine } from '@/lib/audio/engine'
import { usePlayback } from '@/hooks/use-playback'
import PlayGlyph from '@/components/landing/play-glyph'

interface SampleButtonProps {
    member: BandMember
    className?: string
    onPlay?: () => void
}

export const sampleSource = (member: BandMember): PlayableSource => ({
    id: `sample-${member.id}`,
    url: member.sample.url,
    synth: member.sample.synth,
    durationHint: member.sample.durationSeconds
})

// The instrument sample control: one tap plays a short riff or fill, another tap pauses it.
const SampleButton: FC<SampleButtonProps> = ({ member, className, onPlay }): ReactNode => {
    const playback = usePlayback()
    const source = useMemo(() => sampleSource(member), [member])
    const active = playback.activeId === source.id
    const status = active ? playback.status : 'idle'
    const progress = active && playback.duration > 0 ? playback.position / playback.duration : 0

    const label = (() => {
        if (status === 'playing') return `Pause ${member.shortName}'s sample`
        if (status === 'loading') return 'Loading'
        if (status === 'blocked') return 'Tap play again to allow sound'
        if (status === 'error') return 'Sample unavailable. Try again'
        return member.sample.label
    })()

    return (
        <div className={cn('flex flex-col gap-1.5', className)}>
            <button
                type='button'
                onClick={() => {
                    audioEngine.toggle(source)
                    if (status !== 'playing') onPlay?.()
                }}
                onPointerEnter={() => audioEngine.prepare(source)}
                onFocus={() => audioEngine.prepare(source)}
                className={cn(
                    'group relative flex min-h-12 w-full items-center gap-3 overflow-hidden border-2 border-ink px-3 text-left transition-colors xs:w-auto xs:min-w-[280px]',
                    status === 'playing' ? 'bg-ink text-paper' : 'bg-paper-hi text-ink hover:bg-ink hover:text-paper'
                )}
            >
                <span
                    className={cn(
                        'flex size-9 shrink-0 items-center justify-center rounded-full transition-colors',
                        status === 'playing' ? 'bg-bandana text-paper' : 'bg-ink text-paper group-hover:bg-paper group-hover:text-ink'
                    )}
                >
                    <PlayGlyph status={status} />
                </span>

                <span className='flex-1 text-[15px] font-bold leading-tight'>{label}</span>

                <span className='mono-label shrink-0 text-[11px] tabular-nums opacity-70'>
                    {formatTime(active ? playback.position : 0)} / {formatTime(member.sample.durationSeconds)}
                </span>

                <span aria-hidden='true' className='absolute inset-x-0 bottom-0 h-[3px] bg-current/10'>
                    <span
                        className={cn('block h-full origin-left', status === 'playing' ? 'bg-bandana' : 'bg-current')}
                        style={{ transform: `scaleX(${progress})` }}
                    />
                </span>
            </button>

            {!member.sample.url && (
                <span className='mono-label text-[10px] text-ink-soft'>Placeholder sample, not a band recording</span>
            )}
        </div>
    )
}

export default SampleButton
